import React, { useState, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal } from '../hooks/useAnimations';

const categories = [
  { id: 'acne', label: 'Acne', labelHi: 'मुहांसे' },
  { id: 'pigmentation', label: 'Pigmentation', labelHi: 'पिगमेंटेशन' },
  { id: 'scars', label: 'Scars', labelHi: 'निशान' },
  { id: 'hair', label: 'Hair Loss', labelHi: 'बालों का झड़ना' },
];

const resultsData = {
  acne: [
    {
      before: '/acne1-before.jpg',
      after: '/acne1-after.jpg',
      label: 'Vasculitic Acne — Cleared by Dr. Kuldeep Verma',
    },
    {
      before: '/acne2-before.jpg',
      after: '/acne2-after.jpg',
      label: 'Acne Vulgaris — Complete Clearance Treatment',
    },
    {
      before: '/acne3-before.jpg',
      after: '/acne3-after.jpg',
      label: 'Nodular Cystic Acne — Expert Pimple Treatment in Jhansi',
    },
    {
      before: '/acne4-before.jpg',
      after: '/acne4-after.jpg',
      label: 'Severe Pustular Acne — Full Recovery Treatment',
    },
  ],
  pigmentation: [
    {
      before: '/pigment1-before.jpg',
      after: '/pigment1-after.jpg',
      label: 'Vitiligo Surgery — Skin Grafting by Dr. Kuldeep Verma',
    },
    {
      before: '/pigment2-before.jpg',
      after: '/pigment2-after.jpg',
      label: 'Vitiligo Treatment — Facial Depigmentation Recovery',
    },
  ],
  scars: [
    {
      before: '/scar1-before.jpg',
      after: '/scar1-after.jpg',
      label: 'Scar Excision Surgery — Dr. Kuldeep Verma',
    },
    {
      before: '/scar3-before.jpg',
      after: '/scar3-after.jpg',
      label: 'Fat Grafting for Acne Scars — Smooth Skin Results',
    },
    {
      before: '/scar2-before.jpg',
      after: '/scar2-after.jpg',
      label: 'Severe Skin Disease — Complete Healing Treatment',
    },
  ],
  hair: [
    {
      before: '/hair-before.jpg',
      after: '/hair-after.jpg',
      label: 'Male Pattern Baldness — FUE Transplant by Dr. Kuldeep Verma',
    },
    {
      before: '/hair2-before.jpg',
      after: '/hair2-after.jpg',
      label: 'Crown Hair Loss — Complete Hair Transplant Recovery',
    },
  ],
};

function BeforeAfterSlider({ result, index }) {
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const [interacted, setInteracted] = useState(false);
  const [cardRef, isVisible] = useScrollReveal(0.1);

  const updatePosition = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(5, Math.min(clientX - rect.left, rect.width - 5));
    setSliderPos((x / rect.width) * 100);
    if (!interacted) setInteracted(true);
  }, [interacted]);

  // Click anywhere sets slider position immediately
  const onClick = useCallback((e) => {
    updatePosition(e.clientX);
  }, [updatePosition]);

  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    e.target.setPointerCapture?.(e.pointerId);
    isDragging.current = true;
    updatePosition(e.clientX);
  }, [updatePosition]);

  const onPointerMove = useCallback((e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    updatePosition(e.clientX);
  }, [updatePosition]);

  const onPointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  return (
    <div
      ref={cardRef}
      className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className="bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-slate-100 hover:shadow-[0_16px_50px_rgba(0,0,0,0.12)] transition-shadow duration-500">
        {/* Slider area */}
        <div
          ref={containerRef}
          className="relative h-[300px] md:h-[360px] cursor-col-resize select-none overflow-hidden touch-none"
          onClick={onClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid={`before-after-slider-${index}`}
        >
          {/* After image — full width background */}
          <img
            src={result.after}
            alt={`After ${result.label} treatment in Jhansi by Dr. Kuldeep Verma`}
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />

          {/* Before image — clipped by slider position */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
          >
            <img
              src={result.before}
              alt={`Before ${result.label} - best dermatologist in Jhansi`}
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
          </div>

          {/* Slider line + handle */}
          <div
            className="absolute top-0 bottom-0 z-20"
            style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}
          >
            <div className="w-[3px] h-full bg-white shadow-[0_0_12px_rgba(0,0,0,0.4)]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-[0_4px_20px_rgba(0,0,0,0.3)] flex items-center justify-center cursor-col-resize">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-[#475569]">
                <path d="M8 12L4 8M4 8L8 4M4 8H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 12L20 8M20 8L16 4M20 8H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 20L4 16M4 16L8 12M4 16H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0"/>
                <path d="M16 20L20 16M20 16L16 12M20 16H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0"/>
              </svg>
            </div>
          </div>

          {/* Before label */}
          <div className="absolute bottom-3 left-3 z-10 pointer-events-none">
            <div className="px-3 py-1.5 rounded-lg bg-black/50 backdrop-blur-sm">
              <span className="text-white text-[11px] font-manrope font-bold tracking-wider">BEFORE</span>
            </div>
          </div>

          {/* After label */}
          <div className="absolute bottom-3 right-3 z-10 pointer-events-none">
            <div className="px-3 py-1.5 rounded-lg bg-[#D1A760]/80 backdrop-blur-sm">
              <span className="text-white text-[11px] font-manrope font-bold tracking-wider">AFTER</span>
            </div>
          </div>

          {/* Drag hint — fades after first interaction */}
          {!interacted && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 animate-pulse">
              <div className="px-4 py-2 rounded-full bg-black/40 backdrop-blur-sm">
                <span className="text-white text-xs font-manrope tracking-wide">&#x2194; Slide to compare</span>
              </div>
            </div>
          )}
        </div>

        {/* Label */}
        <div className="px-5 py-4">
          <p className="text-sm font-manrope font-medium text-[#0F172A]">{result.label}</p>
          <p className="text-xs text-[#94A3B8] font-manrope mt-1">Treated by Dr. Kuldeep Verma</p>
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfterSection() {
  const { language, t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('acne');
  const [headerRef, headerVisible] = useScrollReveal(0.2);

  const currentResults = resultsData[activeCategory] || [];

  return (
    <section id="results" className="py-20 md:py-32 bg-white" data-testid="before-after-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          ref={headerRef}
          className={`text-center mb-12 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-sm font-manrope font-medium text-[#D1A760] tracking-wide uppercase mb-3">Transformations</p>
          <h2 className="font-outfit font-light text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] tracking-tight" data-testid="results-title">
            {t('results_title')}
          </h2>
          <p className="mt-4 text-base text-[#475569] font-manrope">{t('results_subtitle')}</p>
        </div>

        {/* Category Tabs */}
        <div className="flex justify-center gap-2 md:gap-3 mb-8 md:mb-10 flex-wrap" data-testid="result-categories">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 md:px-6 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-manrope font-medium transition-all duration-300 ${
                activeCategory === cat.id
                  ? 'bg-[#0F172A] text-white shadow-[0_4px_15px_rgba(15,23,42,0.2)]'
                  : 'bg-[#F1F5F9] text-[#475569] hover:bg-slate-200'
              }`}
              data-testid={`result-tab-${cat.id}`}
            >
              {language === 'hi' ? cat.labelHi : cat.label}
            </button>
          ))}
        </div>

        {/* Results Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-testid="results-grid">
          {currentResults.map((result, idx) => (
            <BeforeAfterSlider key={`${activeCategory}-${idx}`} result={result} index={idx} />
          ))}
        </div>

        <p className="text-center mt-8 text-xs text-[#94A3B8] font-manrope">
          Results may vary. Images are representative of treatments provided at Divyadeep Skin Clinic.
        </p>
      </div>
    </section>
  );
}
